import { useState, useEffect } from "react";
import { useDocHubDialog, SnippetDialogData } from "@/contexts/DocHubDialogContext";
import { useBrandSelection } from "@/hooks/useBrandSelection";
import { useDocHubSnippets } from "@/hooks/useDocHubSnippets";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { SnippetsTagSelector } from "@/components/documentation-hub/SnippetsTagSelector";

const LANGUAGES = [
  'JavaScript', 'TypeScript', 'Python', 'Java', 'C++', 'C#', 'PHP', 
  'Ruby', 'Go', 'Rust', 'Swift', 'Kotlin', 'SQL', 'HTML', 'CSS', 
  'Shell', 'PowerShell', 'Bash', 'JSON', 'YAML', 'Markdown', 'Other'
];

interface DocHubCategory {
  id: string;
  brand_id: string;
  name: string;
  description?: string | null;
  color?: string | null;
}

export const SnippetDialog = () => {
  const { currentBrandId } = useBrandSelection();
  const { dialogType, isOpen, snippetData, closeDialog, onSuccess } = useDocHubDialog();
  const { createSnippet, updateSnippet } = useDocHubSnippets(currentBrandId || '');
  
  const [formData, setFormData] = useState<SnippetDialogData>({
    name: '',
    code: '',
    language: '',
    category_id: null,
    tags: [],
    attachments: [],
  });
  const [isUploading, setIsUploading] = useState(false);

  const isEditing = !!snippetData?.id;
  const dialogOpen = dialogType === 'snippet' && isOpen;

  // Fetch categories
  const { data: categories = [] } = useQuery<DocHubCategory[]>({
    queryKey: ['doc-hub-snippets-categories', currentBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_doc_hub_snippets_categories')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('name');
      if (error) throw error;
      return data as DocHubCategory[];
    },
    enabled: !!currentBrandId && dialogOpen,
  });

  // Sync form data when dialog opens
  useEffect(() => {
    if (snippetData && dialogOpen) {
      setFormData({
        id: snippetData.id,
        name: snippetData.name || '',
        code: snippetData.code || '',
        language: snippetData.language || '',
        category_id: snippetData.category_id || null,
        tags: snippetData.tags || [],
        attachments: snippetData.attachments || [],
      });
    }
  }, [snippetData, dialogOpen]);

  const handleFileUpload = async (files: File[]) => {
    if (!currentBrandId) return;
    
    setIsUploading(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${currentBrandId}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('os_doc_hub_snippets')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('os_doc_hub_snippets')
          .getPublicUrl(filePath);

        uploadedUrls.push(data.publicUrl);
      }

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedUrls]
      }));
      
      toast.success('Files uploaded successfully');
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
      'text/*': [],
      'application/json': []
    }
  });

  const handleSubmit = async () => {
    if (!currentBrandId || !formData.name.trim() || !formData.code.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      toast.error('You must be logged in');
      return;
    }

    if (isEditing && formData.id) {
      updateSnippet(formData.id, {
        brand_id: currentBrandId,
        user_id: user.id,
        name: formData.name,
        code: formData.code,
        language: formData.language || null,
        category_id: formData.category_id,
        tags: formData.tags,
        attachments: formData.attachments,
      });
    } else {
      createSnippet({
        brand_id: currentBrandId,
        user_id: user.id,
        name: formData.name,
        code: formData.code,
        language: formData.language || null,
        category_id: formData.category_id,
        tags: formData.tags,
        attachments: formData.attachments
      });
    }

    onSuccess?.();
    closeDialog();
    setFormData({ name: '', code: '', language: '', category_id: null, tags: [], attachments: [] });
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      closeDialog();
      setFormData({ name: '', code: '', language: '', category_id: null, tags: [], attachments: [] });
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleClose} modal={false}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isEditing ? "Edit Snippet" : "Add New Snippet"} 
            popupContext="/documentation-hub/snippets" 
            isOpen={dialogOpen}
          >
            {isEditing ? 'Edit Snippet' : 'Add New Snippet'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Snippet Title *</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              placeholder="Enter snippet title" 
            />
          </div>
          <div>
            <Label htmlFor="category">Category</Label>
            <Select 
              value={formData.category_id || 'none'} 
              onValueChange={(value) => setFormData({ ...formData, category_id: value === 'none' ? null : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Category</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <SnippetsTagSelector
              brandId={currentBrandId || ''}
              selectedTagIds={formData.tags}
              onTagsChange={(tagIds) => setFormData({ ...formData, tags: tagIds })}
            />
          </div>
          <div>
            <Label htmlFor="language">Language</Label>
            <Select 
              value={formData.language || 'none'} 
              onValueChange={(value) => setFormData({ ...formData, language: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select language</SelectItem>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="code">Code *</Label>
            <Textarea 
              id="code" 
              value={formData.code} 
              onChange={(e) => setFormData({ ...formData, code: e.target.value })} 
              placeholder="Paste your code here..." 
              rows={12} 
              className="font-mono text-sm resize-none" 
            />
          </div>
          <div>
            <Label>Attachments (including .zip files)</Label>
            <div 
              {...getRootProps()} 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select (.zip supported)'}
              </p>
            </div>
            {formData.attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {formData.attachments.map((url, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-secondary rounded">
                    <span className="text-sm truncate flex-1">{url.split('/').pop()}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeAttachment(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isUploading}>
              {isEditing ? 'Update' : 'Create'} Snippet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
